import { useCallback, useEffect, useRef, useState } from "react";
import { requestJSON } from "../api";
import type { MonitoringData } from "./model";

const empty = () => ({ data: null, error: false, fetchedAt: null });
const initial = (): MonitoringData => ({
  telemetry: empty(),
  alerts: empty(),
  components: empty(),
  placement: empty(),
  sandbox: empty(),
  changes: empty(),
});
const paths: Record<keyof MonitoringData, string> = {
  telemetry: "/api/v1/monitoring/overview",
  alerts: "/api/v1/monitoring/alerts",
  components: "/api/v1/components",
  placement: "/api/v1/team-runtime",
  sandbox: "/api/v1/sandbox/runtime",
  changes: "/api/v1/monitoring/executions?source=operations",
};

export function useMonitoringData(
  refreshKey: number,
  scope: "monitoring" | "alerts" = "monitoring",
) {
  const [data, setData] = useState<MonitoringData>(initial);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const current = useRef<AbortController>();
  const refresh = useCallback(async () => {
    current.current?.abort();
    const controller = new AbortController();
    current.current = controller;
    setLoading(true);
    await Promise.allSettled(
      (scope === "alerts"
        ? (["alerts", "components", "changes"] as Array<keyof MonitoringData>)
        : (Object.keys(paths) as Array<keyof MonitoringData>)
      ).map(async (key) => {
        try {
          const value = await requestJSON(paths[key], {
            signal: controller.signal,
          });
          if (!controller.signal.aborted)
            setData((previous) => ({
              ...previous,
              [key]: { data: value, error: false, fetchedAt: Date.now() },
            }));
        } catch {
          if (!controller.signal.aborted)
            setData((previous) => ({
              ...previous,
              [key]: { ...previous[key], error: true },
            }));
        }
      }),
    );
    if (!controller.signal.aborted) {
      setLoading(false);
      setNow(Date.now());
    }
  }, [scope]);
  useEffect(() => {
    void refresh();
    return () => current.current?.abort();
  }, [refresh, refreshKey]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
      if (document.visibilityState === "visible") void refresh();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [refresh]);
  return { data, loading, now, refresh };
}
